import React, { useEffect, useState, useCallback } from 'react';
import { communityApi, getAuthSession } from '../services/api';

const DEFAULT_POSTS = [
  {
    _id: 'default_1',
    authorName: 'Dr. Sarah Johnson',
    isVerifiedTherapist: true,
    therapistSpecialization: 'Clinical Psychologist',
    createdAt: new Date(Date.now() - 2 * 3600000).toISOString(),
    category: 'Mental Health',
    content: 'Just completed a mindfulness session with my patients! Remember that pausing for 3 conscious breaths during a stressful workday activates your parasympathetic nervous system.',
    likesCount: 24,
    commentsCount: 5,
    likes: [],
  },
  {
    _id: 'default_2',
    authorName: 'Michael Chen',
    isVerifiedTherapist: false,
    createdAt: new Date(Date.now() - 5 * 3600000).toISOString(),
    category: 'Success Story',
    content: 'Wanted to share my journey overcoming panic attacks through consistent cognitive behavioral therapy. The progress has been life-changing! Never give up on yourself.',
    likesCount: 156,
    commentsCount: 28,
    likes: [],
  },
];

function CommunityPage() {
  const authSession = getAuthSession();
  const currentUserId = authSession?.user?._id || authSession?.user?.id;

  const [posts, setPosts] = useState(DEFAULT_POSTS);
  const [loading, setLoading] = useState(true);
  const [postTitle, setPostTitle] = useState('');
  const [newPost, setNewPost] = useState('');
  const [category, setCategory] = useState('Mental Health');
  const [showPostForm, setShowPostForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prohibited words detection state
  const [detectedProhibited, setDetectedProhibited] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await communityApi.getPosts();
      if (res?.posts && res.posts.length > 0) {
        setPosts(res.posts);
      } else if (res?.posts && Array.isArray(res.posts)) {
        setPosts(res.posts);
      } else {
        setPosts(DEFAULT_POSTS);
      }
    } catch (err) {
      console.warn('Could not load live community posts, using default fallback:', err);
      setPosts(DEFAULT_POSTS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Live real-time scan as user types
  useEffect(() => {
    const fullText = `${postTitle} ${newPost}`.trim();
    if (!fullText) {
      setDetectedProhibited([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsScanning(true);
        const res = await communityApi.verifyText(fullText);
        if (res?.hasProhibitedWords && res.foundWords) {
          setDetectedProhibited(res.foundWords);
        } else {
          setDetectedProhibited([]);
        }
      } catch (err) {
        // Silently catch
      } finally {
        setIsScanning(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [postTitle, newPost]);

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;

    if (!authSession?.token) {
      alert('Please log in or register to publish a post on the Community Wall.');
      window.location.href = '/login';
      return;
    }

    if (detectedProhibited.length > 0) {
      setErrorMessage(`Your post contains prohibited words: ${detectedProhibited.map(w => `"${w.word}"`).join(', ')}. Please remove them.`);
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');

      const res = await communityApi.createPost({
        title: postTitle.trim(),
        content: newPost.trim(),
        category,
      });

      setStatusMessage('✓ Post published to the community wall!');
      setTimeout(() => setStatusMessage(''), 4000);

      setPostTitle('');
      setNewPost('');
      setShowPostForm(false);
      setDetectedProhibited([]);
      if (res?.post) {
        setPosts(prev => [res.post, ...prev.filter(p => p._id !== res.post._id && !String(p._id).startsWith('default_'))]);
      }
      fetchPosts();
    } catch (err) {
      console.error('Failed to post:', err);
      if (err?.isProhibited && err?.foundWords) {
        setDetectedProhibited(err.foundWords);
      }
      setErrorMessage(err.message || 'Failed to submit post.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = async (post) => {
    if (!authSession?.token) {
      alert('Please login to like community posts.');
      return;
    }

    try {
      const res = await communityApi.likePost(post._id);
      setPosts(prev => prev.map(p => {
        if (p._id === post._id) {
          return {
            ...p,
            likesCount: res.likesCount,
            likes: res.isLiked
              ? [...(p.likes || []), currentUserId]
              : (p.likes || []).filter(id => String(id) !== String(currentUserId)),
          };
        }
        return p;
      }));
    } catch (err) {
      // Optimistic toggle fallback
      setPosts(prev => prev.map(p => {
        if (p._id === post._id) {
          return { ...p, likesCount: (p.likesCount || 0) + 1 };
        }
        return p;
      }));
    }
  };

  return (
    <>
      {/* Page Header Start */}
      <section className="page-header">
        <div className="page-header__bg" style={{ backgroundImage: 'url(/assets/images/backgrounds/slider-1-1.jpg)' }}></div>
        <div className="container">
          <div className="page-header__inner">
            <h2 className="page-header__title">Community Wall</h2>
            <ul className="thm-breadcrumb">
              <li><a href="/">Home</a></li>
              <li><span>/</span></li>
              <li><span>Community</span></li>
            </ul>
          </div>
        </div>
      </section>
      {/* Page Header End */}

      {/* Community Section Start */}
      <section className="community-section" style={{
        padding: '80px 0',
        background: 'linear-gradient(120deg, #f8fafc 0%, #e0e7ff 100%)',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-title text-center wow fadeInUp" style={{ marginBottom: '50px' }}>
                <span className="section-title__tagline" style={{ color: '#7c3aed', fontWeight: 600, fontSize: '1.1rem', letterSpacing: '0.04em' }}>Share & Connect</span>
                <h2 className="section-title__title" style={{ fontSize: '2.5rem', fontWeight: 800, color: '#2d2d4d', marginBottom: '18px' }}>Community Wall</h2>
                <p style={{ color: '#555', fontSize: '1.13rem', maxWidth: '520px', margin: '0 auto' }}>
                  A safe, moderated space for mental wellness insights, therapist clinical advice, and peer support.
                </p>
              </div>
            </div>
          </div>

          {statusMessage && (
            <div style={{ maxWidth: '800px', margin: '0 auto 20px', padding: '12px 18px', background: '#ecfdf5', color: '#065f46', borderRadius: '10px', fontWeight: 600, border: '1px solid #a7f3d0' }}>
              {statusMessage}
            </div>
          )}

          {errorMessage && (
            <div style={{ maxWidth: '800px', margin: '0 auto 20px', padding: '12px 18px', background: '#fef2f2', color: '#991b1b', borderRadius: '10px', fontWeight: 600, border: '1px solid #fecaca' }}>
              {errorMessage}
            </div>
          )}

          <div className="row">
            <div className="col-lg-8">
              {/* Create Post Form */}
              <div className="create-post-box wow fadeInUp" style={{
                background: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(6px)',
                padding: '32px',
                borderRadius: '18px',
                boxShadow: '0 6px 32px rgba(80,80,120,0.13)',
                marginBottom: '44px',
                border: '1.5px solid #f3e9ff',
                transition: 'box-shadow 0.2s, transform 0.2s',
              }}>
                {!showPostForm ? (
                  <button
                    onClick={() => {
                      if (!authSession?.token) {
                        alert('Please login or register to share your thoughts on the Community Wall.');
                        window.location.href = '/login';
                        return;
                      }
                      setShowPostForm(true);
                    }}
                    className="thm-btn"
                    style={{ width: '100%', padding: '18px', fontWeight: 700, fontSize: '1.13rem', borderRadius: '10px', boxShadow: '0 2px 8px rgba(124,58,237,0.08)' }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '1.5rem', color: '#7c3aed', fontWeight: 700 }}>+</span> Share Your Thoughts
                    </span>
                  </button>
                ) : (
                  <form onSubmit={handlePostSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>Category:</label>
                        <select
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #d8b4fe', fontSize: '13px', fontWeight: 700, background: '#fff', color: '#7c3aed' }}
                        >
                          <option value="Mental Health">Mental Health</option>
                          <option value="Clinical Insights">Clinical Insights</option>
                          <option value="Therapy & Coping">Therapy & Coping</option>
                          <option value="Mindfulness & Peace">Mindfulness & Peace</option>
                          <option value="Success Story">Success Story</option>
                          <option value="Support & Advice">Support & Advice</option>
                          <option value="General Discussion">General Discussion</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>Title (Optional):</label>
                        <input
                          type="text"
                          value={postTitle}
                          onChange={(e) => setPostTitle(e.target.value)}
                          placeholder="e.g. My daily breathing routine"
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #d8b4fe', fontSize: '13.5px', background: '#fff' }}
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>Your Post / Reflection *</label>
                      <textarea
                        value={newPost}
                        onChange={(e) => setNewPost(e.target.value)}
                        placeholder="What's on your mind? Share your story, insights, or supportive thoughts..."
                        required
                        style={{
                          width: '100%',
                          padding: '18px',
                          borderRadius: '10px',
                          border: detectedProhibited.length > 0 ? '2px solid #ef4444' : '1.5px solid #e0e7ff',
                          fontFamily: 'inherit',
                          fontSize: '1.08rem',
                          minHeight: '120px',
                          marginBottom: '12px',
                          resize: 'vertical',
                          background: 'rgba(255,255,255,0.97)',
                          boxShadow: '0 1px 4px rgba(80,80,120,0.04)',
                        }}
                      />
                    </div>

                    {/* Prohibited words alert box */}
                    {isScanning ? (
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '14px' }}>
                        ⏳ Checking content safety guidelines...
                      </div>
                    ) : detectedProhibited.length > 0 ? (
                      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px' }}>
                        <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#991b1b' }}>
                          ⚠️ Prohibited words detected in your draft:
                        </div>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                          {detectedProhibited.map((w, idx) => (
                            <span key={idx} style={{ padding: '2px 8px', borderRadius: '4px', background: '#fee2e2', color: '#b91c1c', fontSize: '11.5px', fontWeight: 800 }}>
                              🚫 "{w.word}"
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : newPost.trim().length > 5 ? (
                      <div style={{ fontSize: '12px', color: '#059669', fontWeight: 600, marginBottom: '14px' }}>
                        🛡️ Clean: Text adheres to community safety standards.
                      </div>
                    ) : null}

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        type="submit"
                        disabled={isSubmitting || detectedProhibited.length > 0}
                        className="thm-btn"
                        style={{
                          fontWeight: 700,
                          fontSize: '1.08rem',
                          borderRadius: '8px',
                          opacity: detectedProhibited.length > 0 ? 0.6 : 1,
                          cursor: detectedProhibited.length > 0 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isSubmitting ? 'Posting...' : 'Post to Wall'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowPostForm(false);
                          setNewPost('');
                          setPostTitle('');
                          setDetectedProhibited([]);
                        }}
                        className="thm-btn"
                        style={{ backgroundColor: '#ccc', color: '#333', fontWeight: 700, borderRadius: '8px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Posts List */}
              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  Loading community wall...
                </div>
              ) : posts.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', background: '#fff', borderRadius: '14px', border: '1px dashed #cbd5e1' }}>
                  <div style={{ fontSize: '36px', marginBottom: '8px' }}>💬</div>
                  <h4 style={{ margin: '0 0 6px 0', color: '#334155' }}>No community posts found</h4>
                  <p style={{ margin: 0, fontSize: '13.5px', color: '#64748b' }}>
                    Be the first member or practitioner to share your thoughts on the Community Wall!
                  </p>
                </div>
              ) : (
                posts.map((post) => {
                  const authorName = post.authorName || post.author?.name || 'Community Member';
                  const isLikedByMe = currentUserId && (post.likes || []).some(id => String(id) === String(currentUserId));

                  return (
                    <div key={post._id || post.id} className="post-card" style={{
                      visibility: 'visible',
                      opacity: 1,
                      background: 'rgba(255,255,255,0.92)',
                      backdropFilter: 'blur(4px)',
                      padding: '32px',
                      borderRadius: '18px',
                      boxShadow: '0 6px 32px rgba(80,80,120,0.13)',
                      marginBottom: '28px',
                      border: post.isVerifiedTherapist ? '1.5px solid #d8b4fe' : '1.5px solid #f3e9ff',
                      transition: 'box-shadow 0.2s, transform 0.2s',
                    }}>
                      {/* Post Header */}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '18px' }}>
                        <div style={{
                          width: '54px',
                          height: '54px',
                          borderRadius: '50%',
                          background: post.isVerifiedTherapist ? 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' : 'linear-gradient(135deg, #e0e7ff 60%, #fbc2eb 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '18px',
                          boxShadow: '0 2px 8px rgba(124,58,237,0.08)',
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: '20px',
                        }}>
                          {authorName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <h4 style={{ margin: 0, fontWeight: 700, fontSize: '1.08rem', color: '#2d2d4d' }}>
                              {authorName}
                            </h4>
                            {post.isVerifiedTherapist && (
                              <span style={{ fontSize: '11px', background: '#f3e8ff', color: '#7c3aed', padding: '2px 8px', borderRadius: '12px', fontWeight: 800, border: '1px solid #d8b4fe' }}>
                                ✓ Verified Practitioner
                              </span>
                            )}
                          </div>
                          <div style={{ marginTop: '2px' }}>
                            <span style={{ fontSize: '12px', color: '#999', marginRight: '10px' }}>
                              {post.createdAt ? new Date(post.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : post.date || 'Recent'}
                            </span>
                            <span style={{ fontSize: '12px', backgroundColor: '#ede9fe', color: '#7c3aed', padding: '3px 12px', borderRadius: '20px', fontWeight: 600 }}>
                              {post.category || 'General'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Post Content */}
                      {post.title && (
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '1.15rem', fontWeight: 800, color: '#1e293b' }}>
                          {post.title}
                        </h4>
                      )}
                      <p style={{ lineHeight: '1.7', marginBottom: '20px', color: '#444', fontSize: '1.08rem', whiteSpace: 'pre-wrap' }}>
                        {post.content}
                      </p>

                      {/* Post Footer */}
                      <div style={{
                        display: 'flex',
                        gap: '24px',
                        borderTop: '1px solid #eee',
                        paddingTop: '15px',
                        fontSize: '15px',
                        color: '#7c3aed',
                        fontWeight: 600,
                      }}>
                        <button
                          type="button"
                          onClick={() => handleLike(post)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: isLikedByMe ? '#dc2626' : '#7c3aed',
                            fontSize: '15px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <span>{isLikedByMe ? '❤️' : '👍'}</span>
                          <span>{post.likesCount || (post.likes || []).length || 0} Likes</span>
                        </button>
                        <span style={{ color: '#6b7280', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>💬</span>
                          <span>{post.commentsCount || (post.comments || []).length || 0} Comments</span>
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              <div className="community-sidebar wow fadeInUp" style={{
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(4px)',
                padding: '32px',
                borderRadius: '18px',
                boxShadow: '0 6px 32px rgba(80,80,120,0.13)',
                border: '1.5px solid #f3e9ff',
                position: 'sticky',
                top: '110px',
              }}>
                <h3 style={{ marginBottom: '18px', color: '#7c3aed', fontWeight: 800 }}>Community Safety Rules</h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <li style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13.5px', color: '#475569' }}>
                    <span style={{ fontSize: '18px', color: '#059669' }}>✓</span>
                    <span><strong>Verified Practitioners:</strong> Insights from licensed therapists are highlighted with purple verified badges.</span>
                  </li>
                  <li style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13.5px', color: '#475569' }}>
                    <span style={{ fontSize: '18px', color: '#ef4444' }}>🚫</span>
                    <span><strong>Prohibited Words Filter:</strong> Automatic moderation blocks vulgarity, harassment, and unauthorized medical claims.</span>
                  </li>
                  <li style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13.5px', color: '#475569' }}>
                    <span style={{ fontSize: '18px', color: '#7c3aed' }}>🤝</span>
                    <span><strong>Respect & Empathy:</strong> Maintain a kind, supportive atmosphere for all individuals sharing their healing journeys.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Community Section End */}
    </>
  );
}

export default CommunityPage;
